import Axios from "axios";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button, Card, Col, Form, InputGroup, Row } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useStoredPermissions from "../useStoredPermissions";
import { NavBar } from "./../navBar";
import { SideBar } from "./../sideBar";
import { DropDownClients } from "../clients/dropDownClients";
import ClipLoader from "react-spinners/ClipLoader";
import { FaEye, FaSync } from "react-icons/fa";
import {
  getApiResponseMessage,
  getApiErrorMessage,
} from "../../utils/apiMessageHelper";

//theme
import "primereact/resources/themes/lara-light-indigo/theme.css";

//core
import "primereact/resources/primereact.min.css";

import videoAvatar from "../../assets/images/user/video-avatar.png";

// Contexts
import { useNotification } from "../../contexts/NotificationContext";
import { useAuth } from "../../contexts/AuthContext";

// Video frame extraction utilities
import {
  extractVideoFrame,
  getVideoInfo,
  formatDuration,
} from "../../utils/videoFrameExtractor";

export const ListVideos = () => {
  const { t } = useTranslation();
  const permissions = useStoredPermissions() || [];
  const { clientId } = useAuth();
  const { notify } = useNotification();

  // Check if user is Super Admin (clientId === 1)
  const isSuperAdmin = clientId === 1;

  const hasPermissions =
    permissions.includes("Super User") ||
    permissions.includes("Read Video") ||
    permissions.includes("Read Routine");

  //> Constants
  const [data, setData] = useState([]);
  const [videos, setVideos] = useState([]);
  const [newVideo, setNewVideo] = useState([]);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(
    isSuperAdmin ? null : clientId,
  );

  const [showNewVideo, setShowNewVideo] = useState(false);
  const handleShowNewVideo = () => setShowNewVideo(true);
  const handleCloseNewVideo = () => setShowNewVideo(false);
  const [currentVideo, setCurrentVideo] = useState([]);

  const [showDeleteVideoConfirm, setShowDeleteVideoConfirm] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const handleCloseDeleteVideoConfirm = () => setShowDeleteVideoConfirm(false);

  // Edit video modal states
  const [showEditVideo, setShowEditVideo] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [editVideoData, setEditVideoData] = useState({});
  const [editFrameSecond, setEditFrameSecond] = useState(null);
  const [editFramePreview, setEditFramePreview] = useState(null);
  const [editFramePreviewUrl, setEditFramePreviewUrl] = useState(null);
  const [editVideoInfo, setEditVideoInfo] = useState(null);
  const [editProcessingFrame, setEditProcessingFrame] = useState(false);
  const [editVideoFile, setEditVideoFile] = useState(null);
  const [editVideoFileUrl, setEditVideoFileUrl] = useState(null);
  const editVideoFileRef = useRef(null);
  const [forceRender, setForceRender] = useState(0);
  const handleCloseEditVideo = () => {
    setShowEditVideo(false);
    setEditingVideo(null);
    setEditVideoData({});
    setEditFrameSecond(null);
    if (editFramePreviewUrl) {
      URL.revokeObjectURL(editFramePreviewUrl);
    }
    if (editVideoFileUrl) {
      URL.revokeObjectURL(editVideoFileUrl);
    }
    setEditFramePreview(null);
    setEditFramePreviewUrl(null);
    setEditVideoInfo(null);
    setEditVideoFile(null);
    setEditVideoFileUrl(null);
    editVideoFileRef.current = null; // Clear reference too
  };

  // Frame extraction states
  const [frameSecond, setFrameSecond] = useState(2);
  const [framePreview, setFramePreview] = useState(null); // Now stores File object
  const [framePreviewUrl, setFramePreviewUrl] = useState(null); // For display
  const [videoInfo, setVideoInfo] = useState(null);
  const [processingFrame, setProcessingFrame] = useState(false);
  const handleShowDeleteVideoConfirm = (videoId) => {
    setVideoToDelete(videoId);
    setShowDeleteVideoConfirm(true);
  };

  const toast = useRef(null);
  const fileRef = useRef(null);

  // Configure HTML5 validation messages in Spanish
  useEffect(() => {
    const setSpanishValidationMessages = () => {
      const inputs = document.querySelectorAll(
        "input[required], textarea[required]",
      );
      inputs.forEach((input) => {
        input.addEventListener("invalid", function (e) {
          if (this.validity.valueMissing) {
            if (this.name === "name") {
              this.setCustomValidity(t("videos.nameFieldRequired"));
            } else if (this.name === "description") {
              this.setCustomValidity(t("videos.descriptionFieldRequired"));
            } else {
              this.setCustomValidity(t("videos.fieldRequired"));
            }
          } else if (this.validity.tooShort) {
            if (this.name === "name") {
              this.setCustomValidity(t("videos.nameMinLength"));
            } else if (this.name === "description") {
              this.setCustomValidity(t("videos.descriptionMinLength"));
            }
          } else {
            this.setCustomValidity("");
          }
        });

        input.addEventListener("input", function () {
          this.setCustomValidity("");
        });
      });
    };

    // Set messages on mount and when modals open
    setSpanishValidationMessages();

    // Observer to handle dynamically added elements
    const observer = new MutationObserver(() => {
      setSpanishValidationMessages();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  //> STATES
  const handleClientChange = (selected) => {
    if (selected) {
      const newClientId = selected.idClient;
      // Allow setting to null when empty option is selected
      setSelectedClientId(newClientId);
      // Clear videos when empty option is selected
      if (newClientId === null) {
        setVideos([]);
      }
    }
  };

  const handleVideoChange = (object) => {
    const { name, value } = object.target;
    setNewVideo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const videoUploadHandler = async (object) => {
    const file = object.files[0];
    setNewVideo((prev) => ({
      ...prev,
      file: file,
    }));

    // Reset frame states
    setFramePreview(null);
    setFramePreviewUrl(null);
    setVideoInfo(null);

    if (file && file.type.startsWith("video/")) {
      setProcessingFrame(true);
      try {
        // Get video info
        const info = await getVideoInfo(file);
        setVideoInfo(info);

        // Set initial frame time (25% of duration or 2 seconds, whichever is smaller)
        const initialTime = Math.min(1, info.duration / 4);
        setFrameSecond(initialTime);

        // Generate initial frame preview
        const frameFile = await extractVideoFrame(file, initialTime, 640, 480);
        setFramePreview(frameFile);

        // Create URL for display
        const previewUrl = URL.createObjectURL(frameFile);
        setFramePreviewUrl(previewUrl);

        notify(t("videos.framePreviewGenerated"), { type: "success" });
      } catch (error) {
        console.error("Error processing video:", error);
        notify(t("videos.errorProcessingVideoFrame"), { type: "warning" });
      } finally {
        setProcessingFrame(false);
      }
    }
  };

  // Generate new frame when second changes
  const handleFrameSecondChange = async (newSecond) => {
    setFrameSecond(newSecond);

    if (
      newVideo?.file &&
      videoInfo &&
      newSecond >= 0 &&
      newSecond <= videoInfo.duration
    ) {
      setProcessingFrame(true);
      try {
        // Clean up previous preview URL
        if (framePreviewUrl) {
          URL.revokeObjectURL(framePreviewUrl);
        }

        const frameFile = await extractVideoFrame(
          newVideo.file,
          newSecond,
          640,
          480,
        );
        setFramePreview(frameFile);

        // Create new URL for display
        const previewUrl = URL.createObjectURL(frameFile);
        setFramePreviewUrl(previewUrl);
      } catch (error) {
        console.error("Error generating frame:", error);
        notify(t("videos.errorGeneratingFrame"), { type: "warning" });
      } finally {
        setProcessingFrame(false);
      }
    }
  };

  const watchVideo = (video) => {
    try {
      setCurrentVideo(video);
      handleShowNewVideo();
    } catch (e) {}
  };

  // Simple function to get video info from URL
  const getVideoInfoFromUrl = (videoUrl) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.crossOrigin = "anonymous";
      video.muted = true;
      video.preload = "metadata";

      video.addEventListener("loadedmetadata", () => {
        const info = {
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
        };

        // Cleanup
        video.removeAttribute("src");
        video.load();

        resolve(info);
      });

      video.addEventListener("error", () => {
        reject(new Error("Error loading video info"));
      });

      video.src = videoUrl;
    });
  };

  // Handle edit video button click
  const handleEditVideo = async (video) => {
    try {
      setEditingVideo(video);
      setEditVideoData({
        name: video.name,
        description: video.description,
      });

      // Reset frame states
      setEditFrameSecond(0); // Default to 0 seconds
      setEditFramePreview(null);

      // Load current preview if exists
      if (video.idStoragePreview) {
        setEditFramePreviewUrl(video.idStoragePreview);
      } else {
        setEditFramePreviewUrl(null);
      }

      // Show modal first
      setShowEditVideo(true);

      // Try to get video info for frame extraction in background
      if (video.idStorage) {
        try {
          const info = await getVideoInfoFromUrl(video.idStorage);
          setEditVideoInfo(info);
        } catch (error) {
          console.error("Error loading video info:", error);
          setEditVideoInfo(null);
        }
      }
    } catch (error) {
      console.error("Error opening edit modal:", error);
      notify(t("videos.errorOpeningEditModal"), { type: "error" });
    }
  };

  // Handle edit form changes
  const handleEditVideoChange = (object) => {
    const { name, value } = object.target;
    setEditVideoData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle frame second input change (no auto-generation)
  const handleEditFrameSecondChange = (newSecond) => {
    setEditFrameSecond(newSecond);
  };

  // Function to clear video selection and revert to original
  const clearVideoSelection = useCallback(async () => {
    // Clean up file URL
    if (editVideoFileUrl) {
      URL.revokeObjectURL(editVideoFileUrl);
    }

    // Force clear states AND reference immediately
    editVideoFileRef.current = null;
    setEditVideoFile(null);
    setEditVideoFileUrl(null);
    setForceRender((prev) => prev + 1); // Force re-render

    console.log(
      "🧹 Cleared video selection - editVideoFileRef.current:",
      editVideoFileRef.current,
    );

    // Reset to original video info if available
    if (editingVideo?.idStorage) {
      try {
        const info = await getVideoInfoFromUrl(editingVideo.idStorage);
        setEditVideoInfo(info);
        setEditFrameSecond(0);

        // Clear any locally generated previews
        if (
          editFramePreviewUrl &&
          !editFramePreviewUrl.includes("blob.core.windows.net")
        ) {
          URL.revokeObjectURL(editFramePreviewUrl);
        }
        setEditFramePreview(null);
        setEditFramePreviewUrl(null);

        // Restore original preview if it exists
        if (editingVideo.idStoragePreview) {
          setEditFramePreviewUrl(editingVideo.idStoragePreview);
        }
      } catch (error) {
        console.error("Error reverting to original video:", error);
      }
    }
  }, [editVideoFileUrl, editFramePreviewUrl, editingVideo]);

  // Handle edit video file selection
  const handleEditVideoFileSelect = async (event) => {
    console.log("🔄 handleEditVideoFileSelect called with event:", event);

    // Validate event structure to prevent 404 errors
    if (!event || !event.files) {
      console.log("❌ No event or files, returning");
      return;
    }

    const file = event.files[0];
    console.log("📁 File from event:", file ? file.name : "NO FILE");

    // If no file is selected (user clicked but cancelled or deselected), revert to original
    if (!file) {
      console.log("🧹 No file selected, calling clearVideoSelection");
      await clearVideoSelection();
      return;
    }

    // Clean up previous file URL if exists
    if (editVideoFileUrl) {
      URL.revokeObjectURL(editVideoFileUrl);
    }

    // Set both state and reference immediately
    editVideoFileRef.current = file;
    setEditVideoFile(file);
    setForceRender((prev) => prev + 1); // Force re-render

    // Create URL for preview
    const videoUrl = URL.createObjectURL(file);
    setEditVideoFileUrl(videoUrl);

    console.log(
      "📁 New video selected - editVideoFileRef.current:",
      editVideoFileRef.current?.name,
    );

    // Get video info for new file
    try {
      const info = await getVideoInfo(file);
      setEditVideoInfo(info);
      setEditFrameSecond(0); // Reset to default

      // Reset preview states since we have a new video
      if (
        editFramePreviewUrl &&
        !editFramePreviewUrl.includes("blob.core.windows.net")
      ) {
        URL.revokeObjectURL(editFramePreviewUrl);
      }
      setEditFramePreview(null);
      setEditFramePreviewUrl(null);
    } catch (error) {
      notify(t("videos.errorProcessingVideo") + ": " + error.message, {
        type: "warning",
      });
    }
  };

  // Extract frame from video URL
  const extractFrameFromUrl = async (videoUrl, timeInSeconds = 1) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = 640;
      canvas.height = 480;

      let hasResolved = false;
      let playbackStarted = false;

      const extractCurrentFrame = () => {
        try {
          const actualTime = video.currentTime;
          ctx.drawImage(video, 0, 0, 640, 480);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Error generando blob del frame"));
                return;
              }

              const frameFile = new File(
                [blob],
                `frame_${actualTime.toFixed(1)}s.jpg`,
                {
                  type: "image/jpeg",
                  lastModified: Date.now(),
                },
              );

              video.pause();
              video.removeAttribute("src");
              video.load();

              resolve(frameFile);
            },
            "image/jpeg",
            0.9,
          );
        } catch (err) {
          reject(new Error("Error dibujando frame: " + err.message));
        }
      };

      video.addEventListener("loadedmetadata", () => {
        const targetTime = Math.min(
          Math.max(timeInSeconds, 1),
          video.duration - 1,
        );
        video.currentTime = 0;

        const playbackSpeed = targetTime > 20 ? 8.0 : 4.0;
        video.playbackRate = playbackSpeed;
        video
          .play()
          .then(() => {
            playbackStarted = true;
          })
          .catch(() => {
            video.muted = true;
            video
              .play()
              .then(() => {
                playbackStarted = true;
              })
              .catch(() => {
                video.currentTime = targetTime;
              });
          });
      });

      video.addEventListener("timeupdate", () => {
        if (hasResolved || !playbackStarted) return;

        const currentTime = video.currentTime;
        const targetTime = Math.min(
          Math.max(timeInSeconds, 1),
          video.duration - 1,
        );
        const timeRemaining = targetTime - currentTime;

        if (timeRemaining <= 5 && video.playbackRate > 1) {
          video.playbackRate = 1.0;
        } else if (timeRemaining <= 2 && video.playbackRate > 0.5) {
          video.playbackRate = 0.5;
        }

        if (currentTime >= targetTime - 0.1) {
          hasResolved = true;
          video.pause();
          setTimeout(() => {
            extractCurrentFrame();
          }, 300);
        }
      });

      video.addEventListener("seeked", () => {
        if (hasResolved) return;

        const currentTime = video.currentTime;
        const targetTime = Math.min(
          Math.max(timeInSeconds, 1),
          video.duration - 1,
        );

        if (Math.abs(currentTime - targetTime) <= 2.0) {
          hasResolved = true;
          setTimeout(() => {
            extractCurrentFrame();
          }, 200);
        }
      });

      video.addEventListener("error", () => {
        if (hasResolved) return;
        hasResolved = true;
        reject(new Error("Error cargando video"));
      });

      video.muted = true;
      video.preload = "metadata";
      video.crossOrigin = "anonymous";
      video.playsInline = true;
      video.src = videoUrl;
    });
  };

  // Handle refresh button click to generate new frame
  const handleRefreshFrame = async () => {
    if (!editFrameSecond || !editVideoInfo) {
      notify(t("videos.cannotGenerateFrame"), {
        type: "warning",
      });
      return;
    }

    if (editFrameSecond < 0 || editFrameSecond > editVideoInfo.duration) {
      notify(
        "El segundo debe estar entre 0 y " + Math.floor(editVideoInfo.duration),
        { type: "warning" },
      );
      return;
    }

    setEditProcessingFrame(true);

    try {
      // Clean up previous preview URL if it was generated locally
      if (
        editFramePreviewUrl &&
        !editFramePreviewUrl.includes("blob.core.windows.net")
      ) {
        URL.revokeObjectURL(editFramePreviewUrl);
      }

      let frameFile;

      // Use new video file if selected (check reference first for immediate state), otherwise use original video URL
      const currentVideoFile = editVideoFileRef.current;

      if (currentVideoFile) {
        console.log(
          "🎥 Using NEW video file (from ref):",
          currentVideoFile.name,
        );
        frameFile = await extractVideoFrame(
          currentVideoFile,
          editFrameSecond,
          640,
          480,
        );
      } else if (editingVideo?.idStorage) {
        console.log("🎥 Using ORIGINAL video URL:", editingVideo.idStorage);
        frameFile = await extractFrameFromUrl(
          editingVideo.idStorage,
          editFrameSecond,
        );
      } else {
        throw new Error("No hay video disponible para extraer frame");
      }

      setEditFramePreview(frameFile);

      // Create URL for display
      const previewUrl = URL.createObjectURL(frameFile);
      setEditFramePreviewUrl(previewUrl);

      notify("Frame actualizado correctamente", { type: "success" });
    } catch (error) {
      notify("Error generando frame: " + error.message, { type: "warning" });
    } finally {
      setEditProcessingFrame(false);
    }
  };

  //> API CALLS
  async function getVideos() {
    try {
      const url = selectedClientId
        ? `${process.env.REACT_APP_API_URL}/api/v1/Video/list/${selectedClientId}`
        : `${process.env.REACT_APP_API_URL}/api/v1/Video/list`;

      const res = await Axios.get(url);

      if (res.data.length === 0) {
        setData((prev) => ({
          ...prev,
          message: "No hay videos que mostrar.",
        }));
      }
      setVideos(res.data.responseObject);
    } catch (e) {
      setData((prev) => ({
        ...prev,
        message: e.message,
      }));
    }
  }

  const handleNewVideoSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setLoading(true);

    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      setValidated(true);
    } else {
      setValidated(false);

      try {
        var bodyFormData = new FormData();
        bodyFormData.append("name", newVideo.name);
        bodyFormData.append("description", newVideo.description);
        bodyFormData.append("file", newVideo.file);

        // Add video duration if available
        if (videoInfo && videoInfo.duration) {
          bodyFormData.append(
            "duration",
            Math.floor(videoInfo.duration).toString(),
          );
        }

        // Add frame data if available
        if (framePreview && frameSecond !== null) {
          // framePreview is now a File object, append directly
          bodyFormData.append("filePreview", framePreview);
          //bodyFormData.append("frameSecond", frameSecond.toString());
        }

        const base = process.env.REACT_APP_API_URL;
        const postUrl = `${base}/api/v1/Video${
          selectedClientId !== null ? `/${selectedClientId}` : ""
        }`;

        await Axios.post(postUrl, bodyFormData)
          .then(function (res) {
            if (res.status === 200) {
              const message =
                getApiResponseMessage(res) || t("videos.videoAddedSuccess");
              notify(message, { type: "success" });
            }
          })
          .catch(function (error) {
            const errorMessage = getApiErrorMessage(error);
            notify(errorMessage, { type: "error" });
          });
      } catch (e) {
        const errorMessage = getApiErrorMessage(e) || t("videos.videoAddError");
        notify(errorMessage, { type: "error" });
      } finally {
        setLoading(false);
        getVideos();
        setNewVideo(null);
        // Reset frame states and clean up URLs
        if (framePreviewUrl) {
          URL.revokeObjectURL(framePreviewUrl);
        }
        setFramePreview(null);
        setFramePreviewUrl(null);
        setVideoInfo(null);
        setFrameSecond(2);
        fileRef.current.clear();
      }
    }
  };

  async function deleteVideo() {
    if (videoToDelete === null) return;

    try {
      const res = await Axios.delete(
        `${process.env.REACT_APP_API_URL}/api/v1/Video/${videoToDelete}`,
      );

      if (res.status === 200) {
        const message =
          getApiResponseMessage(res) || t("videos.videoDeletedSuccess");
        notify(message, { type: "success" });
      }
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    } finally {
      handleCloseDeleteVideoConfirm();
      getVideos();
    }
  }

  // Handle edit video submit
  const handleEditVideoSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setLoading(true);

    console.log("Starting video update for:", editingVideo.idVideo);

    // Validate required fields
    if (!editVideoData.name || editVideoData.name.trim() === "") {
      notify("El nombre del video es requerido", { type: "warning" });
      setLoading(false);
      return;
    }

    try {
      var bodyFormData = new FormData();

      console.log("editVideoData:", editVideoData);
      console.log("editVideoFile:", editVideoFile?.name || "none");
      console.log("editFramePreview:", editFramePreview ? "present" : "none");

      bodyFormData.append("name", editVideoData.name.trim());
      bodyFormData.append("description", editVideoData.description ?? "");

      // Add video duration if available
      if (editVideoInfo && editVideoInfo.duration) {
        bodyFormData.append(
          "duration",
          Math.floor(editVideoInfo.duration).toString(),
        );
        console.log(
          "Added video duration to FormData:",
          Math.floor(editVideoInfo.duration),
        );
      }

      // Add new video file if selected
      if (editVideoFile) {
        bodyFormData.append("file", editVideoFile);
        console.log("Added video file to FormData");
      }

      // Add frame data if a new frame was generated
      if (editFramePreview && editFrameSecond !== null) {
        bodyFormData.append("filePreview", editFramePreview);
        console.log("Added frame preview to FormData");
      }

      // Debug: Log all FormData entries
      console.log("FormData contents:");
      for (let pair of bodyFormData.entries()) {
        console.log(
          pair[0] +
            ": " +
            (pair[1] instanceof File ? `File: ${pair[1].name}` : pair[1]),
        );
      }

      await Axios.put(
        `${process.env.REACT_APP_API_URL}/api/v1/Video/${editingVideo.idVideo}`,
        bodyFormData,
      )
        .then(function (res) {
          if (res.status === 200) {
            const message =
              getApiResponseMessage(res) || t("videos.videoUpdatedSuccess");
            notify(message, { type: "success" });
          }
        })
        .catch(function (error) {
          console.error("API Error:", error);
          const errorMessage =
            getApiErrorMessage(error) || t("videos.videoUpdateError");
          notify(errorMessage, { type: "error" });
        });
    } catch (e) {
      const errorMessage =
        getApiErrorMessage(e) || t("videos.videoUpdateError");
      notify(errorMessage, { type: "error" });
    } finally {
      setLoading(false);
      getVideos();
      handleCloseEditVideo();
    }
  };

  // Filter Video
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const filteredVideos = videos.filter((video) => {
    const matchesSearch = video.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  //> lifecycles
  useEffect(() => {
    getVideos();
  }, [selectedClientId]);

  return (
    <React.Fragment>
      {hasPermissions && <SideBar />}
      <Container>
        <NavBar />
        <Container>
          <Row>
            <Col xs={12} md={8}>
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <Card.Title as="h5">Gestor de Videos</Card.Title>
                    {isSuperAdmin && (
                      <div style={{ minWidth: "250px" }}>
                        <DropDownClients
                          value={selectedClientId}
                          onChange={handleClientChange}
                          className="form-control"
                        />
                      </div>
                    )}
                  </div>
                </Card.Header>
                <Card.Body>
                  <Container xs={12}>
                    <InputGroup className="mb-3">
                      <InputGroup.Text>Buscar:</InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Ingrese el nombre del video"
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                    </InputGroup>
                    <Row>
                      {filteredVideos.map((video, index) => {
                        return (
                          <Col key={video.idVideo} xs={12} sm={6} lg={4}>
                            <Card
                              style={{
                                position: "relative",
                                backgroundColor:
                                  video.idClient !== null ? "#d4edda" : "white",
                                borderRadius: "10px",
                              }}
                            >
                              {/* Edit button - eye icon in top right corner */}
                              <Button
                                variant="link"
                                className="position-absolute"
                                style={{
                                  top: "8px",
                                  right: "8px",
                                  zIndex: 10,
                                  padding: "4px 8px",
                                  color: "#007bff",
                                }}
                                onClick={() => handleEditVideo(video)}
                                disabled={
                                  !permissions.includes("Add/Update Video")
                                }
                              >
                                <FaEye size={16} />
                              </Button>

                              <Card.Body className="m-t-10">
                                <Row className="align-items-center justify-content-center">
                                  <Col sm="auto">
                                    <Card.Img
                                      src={
                                        video.idStoragePreview || videoAvatar
                                      }
                                    />
                                  </Col>
                                  <Row>
                                    <span className="m-t-35">
                                      {video.name.length > 21
                                        ? `${video.name.substring(0, 21)}...`
                                        : video.name}
                                    </span>
                                    <span className="m-b-0">
                                      {video.description.length > 21
                                        ? `${video.description.substring(
                                            0,
                                            21,
                                          )}...`
                                        : video.description}
                                    </span>
                                    {video.duration > 0 && (
                                      <span
                                        className="m-b-0 text-muted"
                                        style={{ fontSize: "0.9rem" }}
                                      >
                                        Duración:{" "}
                                        {formatDuration(video.duration)}
                                      </span>
                                    )}
                                  </Row>
                                </Row>
                                <Row>
                                  <Col xs={12} md={12} lg={6}>
                                    <Button
                                      className="btn-icon"
                                      style={{ width: "100%" }}
                                      onClick={() => watchVideo(video)}
                                      variant="outline-primary"
                                    >
                                      <i className="feather icon-play mx-1" />
                                    </Button>
                                  </Col>
                                  <Col xs={12} md={12} lg={6}>
                                    <Button
                                      className="btn-icon btn-outline-danger"
                                      style={{ width: "100%" }}
                                      onClick={() =>
                                        handleShowDeleteVideoConfirm(
                                          video.idVideo,
                                        )
                                      }
                                      variant="outline-primary"
                                      disabled={
                                        !permissions.includes("Delete Video")
                                      }
                                    >
                                      <i className="feather icon-trash mx-1" />
                                    </Button>
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </Container>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={4}>
              <Card>
                <Form
                  className="addForm"
                  onSubmit={handleNewVideoSubmit}
                  noValidate
                  validated={validated}
                >
                  <Card.Header>
                    <Card.Title as="h5">Nuevo Video</Card.Title>
                  </Card.Header>
                  <Card.Body>
                    <Container>
                      <Row className="justify-content-md-center">
                        <Col>
                          {/* Name */}
                          <InputGroup className="mb-3">
                            <InputGroup.Text>Nombre</InputGroup.Text>
                            <Form.Control
                              required
                              minLength={3}
                              autoFocus
                              type="text"
                              name="name"
                              value={newVideo?.name || ""}
                              placeholder="Nombre"
                              onChange={handleVideoChange}
                              disabled={loading}
                            />
                          </InputGroup>
                          {/* Description */}
                          <InputGroup className="mb-3">
                            <InputGroup.Text>Descripción</InputGroup.Text>
                            <Form.Control
                              required
                              minLength={2}
                              autoFocus
                              type="text"
                              name="description"
                              value={newVideo?.description || ""}
                              placeholder="Descripción"
                              onChange={handleVideoChange}
                              disabled={loading}
                            />
                          </InputGroup>
                          <div style={{ textAlign: "center" }} className="mb-3">
                            <Toast ref={toast}></Toast>
                            <FileUpload
                              ref={fileRef}
                              xs={4}
                              mode="basic"
                              name="demo[]"
                              accept="video/*"
                              maxFileSize={10000000000}
                              onSelect={videoUploadHandler}
                              disabled={loading}
                              chooseLabel="Seleccionar"
                            />
                          </div>

                          {/* Frame second input */}
                          {videoInfo && (
                            <InputGroup className="mb-3">
                              <InputGroup.Text>
                                Segundo del Frame
                              </InputGroup.Text>
                              <Form.Control
                                type="number"
                                min="0"
                                max={videoInfo.duration}
                                step="0.1"
                                value={frameSecond}
                                onChange={(e) =>
                                  handleFrameSecondChange(
                                    parseFloat(e.target.value),
                                  )
                                }
                                disabled={loading || processingFrame}
                              />
                              <InputGroup.Text
                                className="text-muted"
                                style={{ fontSize: "0.8rem" }}
                              >
                                Max: {formatDuration(videoInfo.duration)}
                              </InputGroup.Text>
                            </InputGroup>
                          )}

                          {/* Frame preview */}
                          {processingFrame && (
                            <div className="text-center mb-3">
                              <ClipLoader size={20} color={"#0d6efd"} />
                              <div className="mt-2 text-muted">
                                Procesando frame...
                              </div>
                            </div>
                          )}

                          {framePreviewUrl && (
                            <div className="text-center mb-3">
                              <div className="text-muted mb-2">
                                Vista previa del frame:
                              </div>
                              <img
                                src={framePreviewUrl}
                                alt="Frame preview"
                                className="img-fluid rounded border"
                                style={{
                                  maxWidth: "200px",
                                  maxHeight: "150px",
                                }}
                              />
                              <div
                                className="mt-1 text-muted"
                                style={{ fontSize: "0.8rem" }}
                              >
                                Segundo: {frameSecond}s
                              </div>
                            </div>
                          )}
                          <Col style={{ marginTop: "15px" }}>
                            <button
                              id="btnLarge"
                              className="btn btn-primary shadow-2 mb-4"
                              type="submit"
                              variant="primary"
                              disabled={
                                !permissions.includes("Add/Update Video") ||
                                newVideo?.description === undefined ||
                                newVideo?.name === undefined ||
                                newVideo?.file === undefined
                              }
                            >
                              {loading ? (
                                <ClipLoader size={20} color={"#ffffff"} />
                              ) : (
                                "Agregar"
                              )}
                            </button>
                          </Col>
                        </Col>
                      </Row>
                    </Container>
                  </Card.Body>
                </Form>
              </Card>
            </Col>
          </Row>

          {/* <!-- Modal display video--> */}
          <Modal show={showNewVideo} onHide={handleCloseNewVideo}>
            <Modal.Header closeButton>
              <Modal.Title>Video {currentVideo.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="justify-content-md-center">
                <video
                  controls
                  autoPlay
                  style={{ width: "100%", height: "400px" }}
                >
                  <source src={currentVideo.idStorage} />
                </video>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseNewVideo}>
                Cerrar
              </Button>
            </Modal.Footer>
          </Modal>

          {/* <!-- Confirmation Modal for Deletion --> */}
          <Modal
            show={showDeleteVideoConfirm}
            onHide={handleCloseDeleteVideoConfirm}
          >
            <Modal.Header closeButton>
              <Modal.Title>Confirmar eliminación</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              ¿Estás seguro de que quieres eliminar este video? Esta acción no
              se puede deshacer.
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={handleCloseDeleteVideoConfirm}
              >
                Cancelar
              </Button>
              <Button variant="danger" onClick={deleteVideo}>
                Eliminar
              </Button>
            </Modal.Footer>
          </Modal>

          {/* <!-- Edit Video Modal --> */}
          <Modal show={showEditVideo} onHide={handleCloseEditVideo} size="lg">
            <Form onSubmit={handleEditVideoSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>Editar Video: {editingVideo?.name}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Container>
                  <Row>
                    <Col md={6}>
                      {/* Preview image */}
                      <div className="text-center mb-3">
                        <div className="text-muted mb-2">Vista previa:</div>
                        {editFramePreviewUrl ? (
                          <img
                            src={editFramePreviewUrl}
                            alt="Video preview"
                            className="img-fluid rounded border"
                            style={{ maxWidth: "250px", maxHeight: "200px" }}
                          />
                        ) : (
                          <div
                            className="d-flex align-items-center justify-content-center rounded border bg-light"
                            style={{
                              width: "250px",
                              height: "200px",
                              margin: "0 auto",
                            }}
                          >
                            <div className="text-muted">
                              Sin preview disponible
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Frame extraction controls */}
                      {editVideoInfo && (
                        <>
                          <InputGroup className="mb-3">
                            <InputGroup.Text>Segundo</InputGroup.Text>
                            <Form.Control
                              type="number"
                              min="0"
                              max={editVideoInfo.duration}
                              step="0.1"
                              value={editFrameSecond || ""}
                              onChange={(e) =>
                                handleEditFrameSecondChange(
                                  parseFloat(e.target.value),
                                )
                              }
                              disabled={loading || editProcessingFrame}
                              placeholder="segundo"
                            />
                            <Button
                              variant="outline-primary"
                              onClick={handleRefreshFrame}
                              disabled={
                                loading ||
                                editProcessingFrame ||
                                !editFrameSecond
                              }
                              style={{ borderLeft: "none" }}
                            >
                              {editProcessingFrame ? (
                                <ClipLoader size={16} color={"#0d6efd"} />
                              ) : (
                                <FaSync size={14} />
                              )}
                            </Button>
                            <InputGroup.Text
                              className="text-muted"
                              style={{ fontSize: "0.8rem" }}
                            >
                              Max: {formatDuration(editVideoInfo.duration)}
                            </InputGroup.Text>
                          </InputGroup>

                          <div className="text-center mb-3 small text-muted">
                            Ingresa un segundo y presiona <FaSync size={12} />{" "}
                            para generar nuevo preview
                          </div>
                        </>
                      )}
                    </Col>

                    <Col md={6}>
                      {/* Name */}
                      <InputGroup className="mb-3">
                        <InputGroup.Text>Nombre</InputGroup.Text>
                        <Form.Control
                          required
                          minLength={3}
                          type="text"
                          name="name"
                          value={editVideoData?.name || ""}
                          placeholder="Nombre"
                          onChange={handleEditVideoChange}
                          disabled={loading}
                        />
                      </InputGroup>

                      {/* Description */}
                      <InputGroup className="mb-3">
                        <InputGroup.Text>Descripción</InputGroup.Text>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          required
                          name="description"
                          value={editVideoData?.description || ""}
                          placeholder="Descripción"
                          onChange={handleEditVideoChange}
                          disabled={loading}
                        />
                      </InputGroup>

                      {/* New Video File Upload */}
                      <div className="text-center mb-3">
                        <div className="text-muted mb-2">
                          Cambiar video (opcional):
                        </div>
                        <FileUpload
                          mode="basic"
                          name="editVideo"
                          accept="video/*"
                          maxFileSize={10000000000}
                          onSelect={handleEditVideoFileSelect}
                          onClear={() => {
                            console.log("🧹 FileUpload onClear triggered");
                            clearVideoSelection();
                          }}
                          disabled={loading}
                          chooseLabel="Seleccionar"
                        />
                      </div>

                      <div className="text-muted small">
                        <strong>Nota:</strong> Puedes cambiar el nombre,
                        descripción, video completo y/o preview.
                      </div>
                    </Col>
                  </Row>
                </Container>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseEditVideo}>
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? (
                    <ClipLoader size={20} color={"#ffffff"} />
                  ) : (
                    "Actualizar"
                  )}
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>
        </Container>
      </Container>
    </React.Fragment>
  );
};
